import React, { useMemo, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { AdditiveBlending, ShaderMaterial, Vector2 } from 'three'
import { PASSTHROUGH_VERT } from '../shaders/passthrough.vert'

interface ShaderFinisherSceneProps {
  fragmentShader: string
  durationMs: number
  /**
   * Burst origin in UV space (0..1). Y is in WebGL convention (origin
   * bottom-left), so screen-top-22% becomes UV y ≈ 0.78. Defaults to screen
   * center.
   */
  centerUv?: [number, number]
}

const ShaderFinisherScene: React.FC<ShaderFinisherSceneProps> = ({
  fragmentShader,
  durationMs,
  centerUv = [0.5, 0.5],
}) => {
  const matRef = useRef<ShaderMaterial>(null)
  const startRef = useRef(performance.now() / 1000)
  const size = useThree((s) => s.size)

  const uniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uDuration: { value: durationMs / 1000 },
      uAspect: { value: new Vector2(1, 1) },
      uCenter: { value: new Vector2(centerUv[0], centerUv[1]) },
    }),
    // We deliberately ignore centerUv changes here — burst origin is frozen
    // at first mount so a mid-cinematic re-render doesn't snap the rings.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [durationMs],
  )

  useFrame(() => {
    if (!matRef.current) return
    const now = performance.now() / 1000
    matRef.current.uniforms.uTime.value = now - startRef.current
    // Aspect-correct UVs so the burst stays circular on tall mobile viewports.
    const aspect = size.width / size.height
    matRef.current.uniforms.uAspect.value.set(aspect >= 1 ? aspect : 1, aspect >= 1 ? 1 : 1 / aspect)
  })

  return (
    <mesh frustumCulled={false}>
      <planeGeometry args={[2, 2]} />
      <shaderMaterial
        ref={matRef}
        vertexShader={PASSTHROUGH_VERT}
        fragmentShader={fragmentShader}
        uniforms={uniforms}
        transparent
        depthTest={false}
        depthWrite={false}
        blending={AdditiveBlending}
      />
    </mesh>
  )
}

export default ShaderFinisherScene
